import {
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  type RowData,
  useReactTable,
} from '@tanstack/react-table';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { TableV2 } from '~/components2/Table';
import projectDetailsStore from '../../../store';
import type { UtilityAgency } from '../../common';
import { isUnreliableProjectShape } from '../../common/estimatedResponseTime';
import store from '../../store';
import { buildAgencyRows } from '../grouping';
import { anyAgencyHasEstTime, compactColumns, expandedColumns } from './columns';

const tableOptions = (isFullUtilitiesExperienceEnabled: boolean) => ({
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
  getExpandedRowModel: getExpandedRowModel(),
  enableSorting: true,
  enableColumnResizing: true,
  columnResizeMode: 'onChange' as const,
  getSubRows: (row: UtilityAgency) => row.subRows,
  initialState: {
    sorting: [{ id: 'agencyName', desc: false }],
    columnVisibility: {
      attachments: isFullUtilitiesExperienceEnabled,
      // Sort-only helper columns backing the Est time header's time / confidence sort modes; never rendered.
      estTimeDays: false,
      estTimeConfidence: false,
    },
  },
});

declare module '@tanstack/react-table' {
  interface TableMeta<TData extends RowData> {
    connectbaseCoverageEnabled?: boolean;
  }
}

export default function TableComponent() {
  // Reading the flag off the snapshot subscribes this component, so cells repaint when flags resolve after
  // the first render; passing it through meta keeps the read from being an unused variable.
  const { agencies, connectbaseCoverageEnabled, dataRequests, isFullUtilitiesExperienceEnabled, project } = useSnapshot(
    store,
  ) as typeof store;
  const { isExpanded } = useSnapshot(projectDetailsStore);

  const data = useMemo(
    () => buildAgencyRows(Object.values(agencies), Object.values(dataRequests)),
    [agencies, dataRequests],
  );

  // Gated behind PostHog (kept in sync with the Select Assets step's est-time gate). Also hide the whole
  // column when every row would render a dash (no requests / no usable stats), or when the project shape
  // makes estimates unreliable (linear/large/scattered projects), per Shane's request.
  const estTimeFlag = useFeatureFlagEnabled('utilities-estimated-response-time');
  const hasEstTimeValues = useMemo(() => anyAgencyHasEstTime(data), [data]);
  // Memoized: the turf area/length walk can traverse thousands of vertices (KMZ imports), so only rerun
  // it when the project changes. Valtio keeps `project`'s reference stable across sort/expand/drawer
  // renders (those live in other stores), so this stays put until the project itself changes.
  const projectShapeUnreliable = useMemo(() => isUnreliableProjectShape(project), [project]);
  const showEstTime = estTimeFlag && hasEstTimeValues && !projectShapeUnreliable;
  const visibleExpandedColumns = useMemo(
    () =>
      showEstTime
        ? expandedColumns
        : expandedColumns.filter((c) => c.id !== 'estTime' && c.id !== 'estTimeDays' && c.id !== 'estTimeConfidence'),
    [showEstTime],
  );

  const compactTable = useReactTable({
    data,
    columns: compactColumns,
    ...tableOptions(isFullUtilitiesExperienceEnabled),
    meta: { connectbaseCoverageEnabled },
  });

  const expandedTable = useReactTable({
    data,
    columns: visibleExpandedColumns,
    ...tableOptions(isFullUtilitiesExperienceEnabled),
    meta: { connectbaseCoverageEnabled },
  });

  if (isExpanded) {
    return <TableV2 key="utilities-expanded" id="utilities-expanded" table={expandedTable} evenOdd={true} />;
  }

  return (
    <TableV2
      key="utilities-compact"
      id="utilities-compact"
      table={compactTable}
      evenOdd={true}
      onRowClick={() => {
        projectDetailsStore.toggleIsExpanded(true);
      }}
    />
  );
}
